<template>
  <section class="fullscreen-section video-section" ref="section">
    <!-- 배경 (검정) -->
    <div class="dark-bg"></div>

    <!-- 확장되는 비디오 프레임 -->
    <div class="video-frame" :class="{ expanded: frameExpanded }">
      <!-- fallback 이미지 배경 -->
      <div class="fallback-bg"></div>
      <video
        ref="video"
        autoplay
        muted
        loop
        playsinline
        class="bg-video"
        @loadeddata="onVideoLoaded"
        @error="onVideoError"
      >
        <source :src="videoSrc" type="video/mp4" />
      </video>
      <div class="video-overlay"></div>
    </div>

    <!-- 콘텐츠 -->
    <div class="section-content video-content" :class="{ visible: contentVisible }" ref="content">
      <p class="sub-title" ref="subTitle">{{ subTitle }}</p>
      <h2 class="main-title" ref="mainTitle">{{ mainTitle }}</h2>
      <p class="description" ref="description">
        {{ description1 }}<br/>
        {{ description2 }}
      </p>
    </div>

    <!-- 좌측 세로 라벨 -->
    <div class="side-label left" :class="{ visible: contentVisible }">
      <span>GUMI HIGHTECH VALLEY</span>
    </div>

  </section>
</template>

<script>
import { gsap } from 'gsap'

export default {
  name: 'VideoSection',
  data() {
    return {
      videoLoaded: false,
      frameExpanded: false,
      contentVisible: false,
      introTimeout: null,
      // 로컬 비디오
      videoSrc: 'https://cdn.coverr.co/videos/coverr-aerial-view-of-city-702/1080p.mp4',
      subTitle: '282만평 구미 하이테크밸리',
      mainTitle: 'PERFECT WORK-LIFE',
      description1: '첨단 산업단지와 함께하는 완벽한 직주근접',
      description2: '출퇴근 10분, 일과 삶의 균형이 시작됩니다'
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.startIntroAnimation()
    })
  },
  beforeUnmount() {
    if (this.introTimeout) {
      clearTimeout(this.introTimeout)
    }
  },
  methods: {
    startIntroAnimation() {
      // 1초 동안 멈춰있다가 프레임 확장 시작
      setTimeout(() => {
        this.frameExpanded = true
      }, 1000)

      // 확장 시작 후 1초 뒤에 콘텐츠 표시 (확장 중에 나옴)
      this.introTimeout = setTimeout(() => {
        this.contentVisible = true
        this.initContentAnimation()
      }, 2000)
    },
    initContentAnimation() {
      // 초기 상태 설정
      gsap.set([this.$refs.subTitle, this.$refs.mainTitle, this.$refs.description], {
        y: 40,
        opacity: 0
      })

      // 순차적으로 애니메이션
      const tl = gsap.timeline({ delay: 0.2 })

      tl.to(this.$refs.subTitle, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out'
      })
      .to(this.$refs.mainTitle, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out'
      }, '-=0.5')
      .to(this.$refs.description, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out'
      }, '-=0.5')
    },
    getSection() {
      return this.$refs.section
    },
    resetToFirst() {
      // 기존 타임아웃 클리어
      if (this.introTimeout) {
        clearTimeout(this.introTimeout)
      }
      // 트랜지션 없이 즉시 초기 상태로
      const frame = this.$el.querySelector('.video-frame')
      if (frame) {
        frame.style.transition = 'none'
        this.frameExpanded = false
        this.contentVisible = false
        // 다음 프레임에서 트랜지션 복구 후 애니메이션 시작
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            frame.style.transition = ''
            this.startIntroAnimation()
          })
        })
      } else {
        this.frameExpanded = false
        this.contentVisible = false
        this.startIntroAnimation()
      }
    },
    setToLast() {
      this.frameExpanded = true
      this.contentVisible = true
    },
    onVideoLoaded() {
      this.videoLoaded = true
    },
    onVideoError() {
      console.log('Video failed to load, using fallback background')
    }
  }
}
</script>

<style scoped>
.fullscreen-section {
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  overflow: hidden;
}

/* ========== 배경 ========== */
.dark-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #0a0a0a;
  z-index: 0;
}

/* ========== 확장되는 비디오 프레임 ========== */
.video-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20%;
  height: 20%;
  overflow: hidden;
  transition: all 3.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 1;
}

.video-frame.expanded {
  width: 100%;
  height: 100%;
}

.fallback-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100vw;
  height: 100vh;
  background: url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80') center/cover no-repeat;
  z-index: 0;
}

.bg-video {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  z-index: 1;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
  z-index: 2;
}

/* ========== 콘텐츠 ========== */
.section-content {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 1400px;
  padding: 0 60px;
  padding-bottom: 120px;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.section-content.visible {
  opacity: 1;
  transform: translateY(0);
}

.video-content {
  text-align: left;
  padding-left: 150px;
}

.sub-title {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
  letter-spacing: 1px;
}

.main-title {
  font-size: 72px;
  font-weight: 700;
  color: #fff;
  line-height: 1.1;
  margin-bottom: 30px;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.description {
  font-size: 18px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.8;
}

/* ========== 사이드 라벨 ========== */
.side-label {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  writing-mode: vertical-rl;
  opacity: 0;
  transition: opacity 0.8s ease;
}

.side-label.visible {
  opacity: 1;
}

.side-label.left {
  left: 40px;
  transform: translateY(-50%) rotate(180deg);
}


.side-label span {
  font-size: 11px;
  letter-spacing: 3px;
  color: rgba(255, 255, 255, 0.3);
  font-weight: 500;
  text-transform: uppercase;
}

/* ========== 반응형 ========== */
@media (max-width: 1400px) {
  .video-content {
    padding-left: 40px;
  }
  .main-title {
    font-size: 60px;
  }
}

@media (max-width: 1200px) {
  .main-title {
    font-size: 52px;
  }
  .sub-title {
    font-size: 16px;
  }
}

@media (max-width: 1024px) {
  .side-label {
    display: none;
  }
  .section-content {
    padding: 0 40px;
    padding-bottom: 100px;
  }
  .video-content {
    padding-left: 0;
  }
  .main-title {
    font-size: 42px;
  }
}

@media (max-width: 768px) {
  .fullscreen-section {
    align-items: flex-end;
  }
  .section-content {
    padding: 0 24px;
    padding-bottom: 80px;
  }
  .main-title {
    font-size: 28px;
    letter-spacing: 1px;
  }
  .sub-title {
    font-size: 14px;
  }
  .description {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .main-title {
    font-size: 24px;
  }
  .section-content {
    padding-bottom: 60px;
  }
}
</style>
